import { generateCId } from "@/pages/taskpane/lib/utils/randomUUID";
import { EBusyStatus, EStatusResource } from "./enum";
import { parseISOString, timeToMinutes } from "../../ulti";

export class AttendeeInfo {
  id: string;
  email: string;
  name: string;
  meetings: AttendeeCalendarEvent[];
  isExternal?: boolean;
  isRequired?: boolean;
  currentDate?: string;
  total?: number;
  noOfAvailable?: number;
  isBusy?: boolean;
  isHoliday?: boolean;
  workingHours?: {
    startTime: string;
    endTime: string;
  };

  constructor(data?: IAttendeeInfo) {
    this.id = generateCId();
    this.email = data?.userEmail ?? "";
    this.name = data?.name ?? "";
    this.meetings = data?.calendarEvents?.map((event) => new AttendeeCalendarEvent(event, data?.currentDate)) ?? [];
    this.isRequired = !!data?.isRequired;
    this.currentDate = data?.currentDate ?? "";
    this.isExternal = data?.isExternal ?? false;
    this.isBusy = data?.isBusy ?? false;
    this.isHoliday = data?.isHoliday ?? false;
    this.workingHours = data?.workingHours;
  }

  status(startTime: string, endTime: string, isRecurring: boolean, isAllDayMeeting: boolean): EStatusResource {
    if (this.isExternal) {
      return EStatusResource.Disable;
    } else if (isRecurring) {
      return this.isBusy ? EStatusResource.Unavailable : EStatusResource.Available;
    } else if (this.isHoliday) {
      return EStatusResource.Unavailable;
    } else if (isAllDayMeeting) {
      return EStatusResource.Available;
    }
    if (this.workingHours) {
      const startParsed = parseISOString(startTime);
      const endParsed = parseISOString(endTime);
      // Parse workingHours (format HH:mm) to minutes since midnight
      const parseHHmm = (timeStr: string): number => {
        const [hours, minutes] = timeStr.split(":").map(Number);
        return (hours || 0) * 60 + (minutes || 0);
      };

      const workingStartMinutes = parseHHmm(this.workingHours.startTime);
      const workingEndMinutes = parseHHmm(this.workingHours.endTime);

      // Get time portion from parsed meeting times
      const meetingStartMinutes = timeToMinutes(startParsed);
      const meetingEndMinutes = timeToMinutes(endParsed);

      // Check if either meeting startTime or endTime is outside workingHours
      // Meeting is outside if startTime is before workingHours start OR after workingHours end
      // OR if endTime is before workingHours start OR after workingHours end
      const isStartOutside = meetingStartMinutes < workingStartMinutes || meetingStartMinutes >= workingEndMinutes;
      const isEndOutside = meetingEndMinutes < workingStartMinutes || meetingEndMinutes > workingEndMinutes;

      if (isStartOutside || isEndOutside) {
        return EStatusResource.Unavailable;
      }
    } else if (this.meetings.length === 0) {
      return EStatusResource.Available;
    }
    // Nếu có meetings, kiểm tra xem có meeting nào unavailable không
    const hasUnavailable = this.meetings.some(
      (meeting) => meeting.statusMeeting(startTime, endTime) === EStatusResource.Unavailable
    );
    return hasUnavailable ? EStatusResource.Unavailable : EStatusResource.Available;
  }

  toJson(): IAttendeeInfo {
    return {
      name: this.name,
      userEmail: this.email,
      availabilityMask: "00000000000000000000000000000000",
      calendarEvents: this.meetings.map(meeting => meeting.toJson()),
      isRequired: this.isRequired,
      currentDate: this.currentDate,
      total: this.total,
      noOfAvailable: this.noOfAvailable,
      isBusy: this.isBusy,
      isHoliday: this.isHoliday,
      workingHours: this.workingHours,
      isExternal: this.isExternal,
    }
  }
}

export class AttendeeCalendarEvent {
  id: string;
  startTime: string;
  endTime: string;
  busyStatus: EBusyStatus;

  constructor(data?: Partial<AttendeeCalendarEvent>, currentDate?: string, index?: number) {
    this.startTime = data?.startTime ?? "";
    this.endTime = data?.endTime ?? "";
    this.busyStatus = data?.busyStatus ?? EBusyStatus.Free;
    this.id = currentDate
      ? `${this.busyStatus}-${index ?? 0}-uuid-${currentDate}-${this.startTime}-${this.endTime}`
      : generateCId();
  }

  statusMeeting(
    startTime: string,
    endTime: string,
  ): EStatusResource {
    // Helper function để so sánh hai ParsedDateTime
    const compareDateTime = (a: ReturnType<typeof parseISOString>, b: ReturnType<typeof parseISOString>): number => {
      if (a.year !== b.year) return a.year - b.year;
      if (a.month !== b.month) return a.month - b.month;
      if (a.day !== b.day) return a.day - b.day;
      if (a.hours !== b.hours) return a.hours - b.hours;
      if (a.minutes !== b.minutes) return a.minutes - b.minutes;
      return a.seconds - b.seconds;
    };

    // Chuyển đổi string thành ParsedDateTime để so sánh
    const startParsed = parseISOString(startTime);
    const endParsed = parseISOString(endTime);

    const meetingStartParsed = parseISOString(this.startTime);
    const meetingEndParsed = parseISOString(this.endTime);

    const startTimeInMeeting =
      compareDateTime(startParsed, meetingStartParsed) >= 0 && compareDateTime(startParsed, meetingEndParsed) < 0;
    const endTimeInMeeting =
      compareDateTime(endParsed, meetingStartParsed) > 0 && compareDateTime(endParsed, meetingEndParsed) <= 0;
    const coversMeeting =
      compareDateTime(startParsed, meetingStartParsed) <= 0 && compareDateTime(endParsed, meetingEndParsed) >= 0;
    const isOverlap = startTimeInMeeting || endTimeInMeeting || coversMeeting;

    // Nếu có overlap và busyStatus là Busy, OutOfOffice, hoặc Tentative thì return Unavailable
    if (isOverlap) {
      if (
        this.busyStatus === EBusyStatus.Busy ||
        this.busyStatus === EBusyStatus.OutOfOffice ||
        this.busyStatus === EBusyStatus.Tentative
      ) {
        return EStatusResource.Unavailable;
      }
    }

    // Nếu không overlap hoặc busyStatus không phải là Busy/OutOfOffice/Tentative, return Available
    return EStatusResource.Available;
  }

  toJson(): Partial<AttendeeCalendarEvent> {
    return {
      id: this.id,
      startTime: this.startTime,
      endTime: this.endTime,
      busyStatus: this.busyStatus,
    }
  }
}

export interface IAttendeeInfo {
  name: string;
  userEmail: string;
  availabilityMask?: string;
  calendarEvents?: Partial<AttendeeCalendarEvent>[];
  isRequired?: boolean;
  currentDate?: string;
  total?: number;
  noOfAvailable?: number;
  isBusy?: boolean;
  isHoliday?: boolean;
  workingHours?: {
    startTime: string;
    endTime: string;
  };
  isExternal?: boolean;
}

export interface IGetAttendeeStatusRequest {
  userEmails: string[];
  occurrences: { startDate: string; endDate: string }[];
  timeZoneOccurrence: string; // Normal thì truyền timezone client = timeZoneStandardName, recurring thì truyền UTC
  interval: number;
  timeZoneStandardName: string; //timezone outlook
  isRecurring: boolean;
}
